import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { Loader2, X } from 'lucide-react';

const SECTORS = ['mining', 'processing', 'infrastructure', 'industrial', 'services', 'other'] as const;
const LANGUAGES = ['en', 'fr', 'pt', 'sw', 'ln'] as const;
const OPEN_TO = ['employment', 'consulting', 'board_roles', 'projects'] as const;

export default function ProfessionalProfileSetup() {
  const { t } = useTranslation();
  const { user, role, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [existingId, setExistingId] = useState<string | null>(null);

  const [headline, setHeadline] = useState('');
  const [bio, setBio] = useState('');
  const [skills, setSkills] = useState<string[]>([]);
  const [skillInput, setSkillInput] = useState('');
  const [sectors, setSectors] = useState<string[]>([]);
  const [languages, setLanguages] = useState<string[]>([]);
  const [yearsExperience, setYearsExperience] = useState('');
  const [openTo, setOpenTo] = useState<string[]>([]);
  const [linkedinUrl, setLinkedinUrl] = useState('');

  useEffect(() => {
    if (!authLoading && (!user || role !== 'professional')) {
      navigate('/auth');
      return;
    }

    if (user) {
      fetchExistingProfile();
    }
  }, [user, role, authLoading, navigate]);

  const fetchExistingProfile = async () => {
    if (!user) return;

    try {
      const { data } = await supabase
        .from('professionals')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (data) {
        setExistingId(data.id);
        setHeadline(data.headline || '');
        setBio(data.bio || '');
        setSkills((data.skills as string[]) || []);
        setSectors((data.sectors as string[]) || []);
        setLanguages((data.languages as string[]) || []);
        setYearsExperience(data.years_experience?.toString() || '');
        setOpenTo((data.open_to as string[]) || []);
        setLinkedinUrl(data.linkedin_url || '');
      }
    } catch (error) {
      // No existing profile
    } finally {
      setLoading(false);
    }
  };

  const addSkill = () => {
    const skill = skillInput.trim();
    if (skill && !skills.includes(skill)) {
      setSkills([...skills, skill]);
      setSkillInput('');
    }
  };

  const removeSkill = (skill: string) => {
    setSkills(skills.filter(s => s !== skill));
  };

  const toggleItem = (
    item: string,
    array: string[],
    setter: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    setter(prev =>
      prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setSaving(true);

    try {
      const professionalData = {
        user_id: user.id,
        headline: headline || null,
        bio: bio || null,
        skills,
        sectors,
        languages,
        years_experience: yearsExperience ? parseInt(yearsExperience) : null,
        open_to: openTo,
        linkedin_url: linkedinUrl || null,
      };

      if (existingId) {
        const { error } = await supabase
          .from('professionals')
          .update(professionalData)
          .eq('id', existingId);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('professionals')
          .insert(professionalData);

        if (error) throw error;
      }

      toast({ title: 'Profile saved successfully' });
      navigate('/dashboard/professional');
    } catch (error) {
      console.error('Error saving profile:', error);
      toast({ title: 'Error saving profile', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || loading) {
    return (
      <Layout>
        <div className="container flex min-h-[50vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container max-w-2xl py-8">
        <Card>
          <CardHeader>
            <CardTitle>{t('professional.profile.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="headline">{t('professional.profile.headline')}</Label>
                <Input
                  id="headline"
                  value={headline}
                  onChange={(e) => setHeadline(e.target.value)}
                  placeholder={t('professional.profile.headlinePlaceholder')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="bio">{t('professional.profile.bio')}</Label>
                <Textarea
                  id="bio"
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  rows={4}
                />
              </div>

              <div className="space-y-2">
                <Label>{t('professional.profile.skills')}</Label>
                <div className="flex gap-2">
                  <Input
                    value={skillInput}
                    onChange={(e) => setSkillInput(e.target.value)}
                    placeholder={t('professional.profile.skillsPlaceholder')}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                  />
                  <Button type="button" variant="outline" onClick={addSkill}>
                    Add
                  </Button>
                </div>
                {skills.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {skills.map(skill => (
                      <span
                        key={skill}
                        className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-sm"
                      >
                        {skill}
                        <button
                          type="button"
                          onClick={() => removeSkill(skill)}
                          className="hover:text-destructive"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <Label>{t('professional.profile.sectors')}</Label>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {SECTORS.map(sector => (
                    <Label
                      key={sector}
                      className={`flex cursor-pointer items-center gap-2 rounded-lg border p-3 transition-colors ${
                        sectors.includes(sector) ? 'border-primary bg-primary/5' : 'border-muted'
                      }`}
                    >
                      <Checkbox
                        checked={sectors.includes(sector)}
                        onCheckedChange={() => toggleItem(sector, sectors, setSectors)}
                      />
                      <span className="text-sm">{t(`sectors.${sector}`)}</span>
                    </Label>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <Label>{t('professional.profile.languages')}</Label>
                <div className="flex flex-wrap gap-3">
                  {LANGUAGES.map(lang => (
                    <Label
                      key={lang}
                      className={`flex cursor-pointer items-center gap-2 rounded-lg border px-4 py-2 transition-colors ${
                        languages.includes(lang) ? 'border-primary bg-primary/5' : 'border-muted'
                      }`}
                    >
                      <Checkbox
                        checked={languages.includes(lang)}
                        onCheckedChange={() => toggleItem(lang, languages, setLanguages)}
                      />
                      <span className="text-sm">{t(`languages.${lang}`)}</span>
                    </Label>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="experience">{t('professional.profile.experience')}</Label>
                <Input
                  id="experience"
                  type="number"
                  min="0"
                  max="50"
                  value={yearsExperience}
                  onChange={(e) => setYearsExperience(e.target.value)}
                  className="max-w-[120px]"
                />
              </div>

              <div className="space-y-3">
                <Label>{t('professional.profile.openTo')}</Label>
                <div className="grid grid-cols-2 gap-3">
                  {OPEN_TO.map(item => (
                    <Label
                      key={item}
                      className={`flex cursor-pointer items-center gap-2 rounded-lg border p-3 transition-colors ${
                        openTo.includes(item) ? 'border-primary bg-primary/5' : 'border-muted'
                      }`}
                    >
                      <Checkbox
                        checked={openTo.includes(item)}
                        onCheckedChange={() => toggleItem(item, openTo, setOpenTo)}
                      />
                      <span className="text-sm">{t(`openTo.${item}`)}</span>
                    </Label>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="linkedin">{t('professional.profile.linkedin')}</Label>
                <Input
                  id="linkedin"
                  type="url"
                  value={linkedinUrl}
                  onChange={(e) => setLinkedinUrl(e.target.value)}
                  placeholder="https://linkedin.com/in/..."
                />
              </div>

              <Button type="submit" className="w-full" disabled={saving}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('professional.profile.save')}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}